import React from "react";
import {
  Typography,
  Box,
  CircularProgress,
  TextField,
  Checkbox,
  Slider,
  IconButton,
  Tooltip,
  Button,
  Radio,
  FormControlLabel,
} from "@material-ui/core";
import { GetApp, Fullscreen, FullscreenExit } from "@material-ui/icons";
import { css } from "@emotion/css";
import { useQuestion, useBook, useQuestionGroup } from "../queries";
import { healthFormsApi } from "../api";
import type { Selection } from "./Sidebar";
import { Question, QuestionResponseType } from "../types/common";
import { Book, QuestionGroup } from "../types/persisted";
import { useFeelEvaluator } from "./useFeelEvaluator";
import { ResponseValue, QuestionRefWithId } from "./types";
import { shouldShowQuestionInTestMode } from "./testModeUtils";

type PreviewProps = {
  selection: Selection;
  isExpanded: boolean;
  onToggleExpand: () => void;
  editedEntity: Question | Book | QuestionGroup | null;
  showTestToggle: boolean;
};

type ResponsesContextType = {
  responses: Record<number, ResponseValue>;
  setResponse: (questionId: number, value: ResponseValue) => void;
  autoFillTrigger: number;
  clearTrigger: number;
  registerQuestion: (questionId: number, semanticKey: string | undefined) => void;
  testMode: boolean;
  semanticKeyMap: Record<number, string>;
  allQuestions: QuestionRefWithId[];
};

const ResponsesContext = React.createContext<ResponsesContextType>({
  responses: {},
  setResponse: () => {},
  autoFillTrigger: 0,
  clearTrigger: 0,
  registerQuestion: () => {},
  testMode: false,
  semanticKeyMap: {},
  allQuestions: [],
});

const useResponses = () => React.useContext(ResponsesContext);

const generateAutoFillValue = (responseType: QuestionResponseType): ResponseValue => {
  if (!responseType) return null;

  switch (responseType._type) {
    case "select":
      return responseType.variants[0] ?? null;
    case "select-or-other":
      return responseType.variants[0] ?? null;
    case "multi-select":
      return responseType.variants.slice(0, Math.min(2, responseType.variants.length));
    case "multi-select-grouped": {
      const allVariants: Array<{ value: string; group: string }> = [];
      for (const group of responseType.groups) {
        for (const variant of group.variants.slice(0, 1)) {
          allVariants.push({ value: variant, group: group.label });
        }
      }
      return allVariants.length > 0 ? allVariants.slice(0, 2) : null;
    }
    case "checkbox":
      return true;
    case "vas":
    case "number-range":
    case "integer":
      return Math.floor((responseType.min + responseType.max) / 2);
    case "date":
      return new Date().toISOString().split("T")[0];
    case "text":
      return "Sample text";
    default:
      return null;
  }
};

const downloadJson = (data: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ResponseTypePreview: React.VFC<{ questionId: number; responseType: QuestionResponseType }> = ({
  questionId,
  responseType,
}) => {
  const { setResponse, autoFillTrigger, clearTrigger } = useResponses();
  const [selectedValue, setSelectedValue] = React.useState<string | null>(null);
  const [otherValue, setOtherValue] = React.useState<string>("");
  const [checkedValues, setCheckedValues] = React.useState<Set<string>>(new Set());
  const [checkboxValue, setCheckboxValue] = React.useState<boolean | null>(null);
  const [sliderValue, setSliderValue] = React.useState<number | null>(null);
  const [integerValue, setIntegerValue] = React.useState<string>("");
  const [dateValue, setDateValue] = React.useState<string>("");
  const [textValue, setTextValue] = React.useState<string>("");

  React.useEffect(() => {
    setSelectedValue(null);
    setOtherValue("");
    setCheckedValues(new Set());
    setCheckboxValue(null);
    setSliderValue(null);
    setIntegerValue("");
    setDateValue("");
    setTextValue("");
  }, [responseType]);

  React.useEffect(() => {
    if (autoFillTrigger === 0) return;
    if (!responseType) return;

    const value = generateAutoFillValue(responseType);
    switch (responseType._type) {
      case "select":
        setSelectedValue(value as string);
        break;
      case "select-or-other":
        setSelectedValue(value as string);
        setOtherValue("");
        break;
      case "multi-select":
        setCheckedValues(new Set(value as string[]));
        break;
      case "multi-select-grouped": {
        const groupedValues = value as Array<{ value: string; group: string }> | null;
        if (groupedValues) {
          setCheckedValues(new Set(groupedValues.map((v) => v.value)));
        }
        break;
      }
      case "checkbox":
        setCheckboxValue(value as boolean);
        break;
      case "vas":
      case "number-range":
        setSliderValue(value as number);
        break;
      case "integer":
        setIntegerValue(String(value));
        break;
      case "date":
        setDateValue(value as string);
        break;
      case "text":
        setTextValue(value as string);
        break;
    }
  }, [autoFillTrigger, responseType]);

  React.useEffect(() => {
    if (clearTrigger === 0) return;
    setSelectedValue(null);
    setOtherValue("");
    setCheckedValues(new Set());
    setCheckboxValue(null);
    setSliderValue(null);
    setIntegerValue("");
    setDateValue("");
    setTextValue("");
  }, [clearTrigger, responseType]);

  React.useEffect(() => {
    if (!responseType) return;

    let value: ResponseValue = null;
    switch (responseType._type) {
      case "select":
        value = selectedValue;
        break;
      case "select-or-other":
        value = otherValue ? { other: otherValue } : selectedValue;
        break;
      case "multi-select":
        value = checkedValues.size > 0 ? Array.from(checkedValues) : null;
        break;
      case "multi-select-grouped":
        if (checkedValues.size > 0) {
          const result: Array<{ value: string; group: string }> = [];
          for (const group of responseType.groups) {
            for (const variant of group.variants) {
              if (checkedValues.has(variant)) {
                result.push({ value: variant, group: group.label });
              }
            }
          }
          value = result.length > 0 ? result : null;
        }
        break;
      case "checkbox":
        value = checkboxValue !== null ? checkboxValue : null;
        break;
      case "vas":
      case "number-range":
        value = sliderValue !== null ? sliderValue : null;
        break;
      case "integer":
        value = integerValue ? parseInt(integerValue, 10) : null;
        break;
      case "date":
        value = dateValue || null;
        break;
      case "text":
        value = textValue || null;
        break;
    }
    setResponse(questionId, value);
  }, [
    questionId,
    responseType,
    responseType?._type,
    selectedValue,
    otherValue,
    checkedValues,
    checkboxValue,
    sliderValue,
    integerValue,
    dateValue,
    textValue,
    setResponse,
  ]);

  if (!responseType) {
    return <Typography color="error">Question responseType is undefined</Typography>;
  }

  switch (responseType._type) {
    case "select":
      return (
        <div className={styles.selectContainer}>
          {responseType.variants.map((v, i) => (
            <label key={i} className={styles.radioOption} onClick={() => setSelectedValue(v)}>
              <Radio
                checked={selectedValue === v}
                onChange={() => setSelectedValue(v)}
                color="primary"
                className={styles.radioButton}
              />
              <span className={styles.radioLabel}>{v}</span>
            </label>
          ))}
        </div>
      );

    case "select-or-other":
      return (
        <div className={styles.selectContainer}>
          {responseType.variants.map((v, i) => (
            <label
              key={i}
              className={styles.radioOption}
              onClick={() => {
                setSelectedValue(v);
                setOtherValue("");
              }}
            >
              <Radio
                checked={selectedValue === v && !otherValue}
                onChange={() => {
                  setSelectedValue(v);
                  setOtherValue("");
                }}
                color="primary"
                className={styles.radioButton}
              />
              <span className={styles.radioLabel}>{v}</span>
            </label>
          ))}
          <div className={styles.otherField}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Other (please specify):
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Please specify"
              value={otherValue}
              onChange={e => {
                setOtherValue(e.target.value);
                setSelectedValue(null);
              }}
            />
          </div>
        </div>
      );

    case "multi-select":
      return (
        <div className={styles.multiSelectContainer}>
          {responseType.variants.map((v, i) => (
            <label key={i} className={styles.checkboxOption}>
              <Checkbox
                checked={checkedValues.has(v)}
                onChange={e => {
                  const newSet = new Set(checkedValues);
                  if (e.target.checked) {
                    newSet.add(v);
                  } else {
                    newSet.delete(v);
                  }
                  setCheckedValues(newSet);
                }}
                color="primary"
                className={styles.checkboxButton}
              />
              <span className={styles.checkboxLabel}>{v}</span>
            </label>
          ))}
        </div>
      );

    case "multi-select-grouped":
      return (
        <div className={styles.multiSelectContainer}>
          {responseType.groups.map((group, groupIdx) => (
            <div key={groupIdx} className={styles.groupSection}>
              <Typography variant="subtitle2" className={styles.groupHeader}>
                {group.label}
              </Typography>
              {group.variants.map((v, i) => (
                <label key={i} className={styles.checkboxOption}>
                  <Checkbox
                    checked={checkedValues.has(v)}
                    onChange={(e) => {
                      const newSet = new Set(checkedValues);
                      if (e.target.checked) {
                        newSet.add(v);
                      } else {
                        newSet.delete(v);
                      }
                      setCheckedValues(newSet);
                    }}
                    color="primary"
                    className={styles.checkboxButton}
                  />
                  <span className={styles.checkboxLabel}>{v}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
      );

    case "checkbox":
      return (
        <div className={styles.booleanContainer}>
          <label className={styles.booleanOption} onClick={() => setCheckboxValue(true)}>
            <Radio
              checked={checkboxValue === true}
              onChange={() => setCheckboxValue(true)}
              color="primary"
              className={styles.radioButton}
            />
            <span className={styles.radioLabel}>Yes</span>
          </label>
          <label className={styles.booleanOption} onClick={() => setCheckboxValue(false)}>
            <Radio
              checked={checkboxValue === false}
              onChange={() => setCheckboxValue(false)}
              color="primary"
              className={styles.radioButton}
            />
            <span className={styles.radioLabel}>No</span>
          </label>
        </div>
      );

    case "vas":
      return (
        <div className={styles.vasContainer} style={{ opacity: sliderValue === null ? 0.4 : 1 }}>
          <div className={styles.vasLabels}>
            <span>{responseType.min}</span>
            <span>{responseType.max}</span>
          </div>
          <Slider
            value={sliderValue !== null ? sliderValue : Math.floor((responseType.min + responseType.max) / 2)}
            min={responseType.min}
            max={responseType.max}
            onChange={(_, value) => setSliderValue(value as number)}
            valueLabelDisplay={sliderValue !== null ? "on" : "auto"}
          />
        </div>
      );

    case "integer":
      return (
        <div className={styles.integerContainer}>
          <TextField
            type="number"
            variant="outlined"
            size="small"
            inputProps={{ min: responseType.min, max: responseType.max }}
            placeholder={`${responseType.min} - ${responseType.max}`}
            fullWidth
            value={integerValue}
            onChange={e => setIntegerValue(e.target.value)}
          />
        </div>
      );

    case "number-range":
      return (
        <div className={styles.vasContainer} style={{ opacity: sliderValue === null ? 0.4 : 1 }}>
          <div className={styles.vasLabels}>
            <span>{responseType.min}</span>
            <span>{responseType.max}</span>
          </div>
          <Slider
            value={sliderValue !== null ? sliderValue : Math.floor((responseType.min + responseType.max) / 2)}
            min={responseType.min}
            max={responseType.max}
            onChange={(_, value) => setSliderValue(value as number)}
            valueLabelDisplay={sliderValue !== null ? "on" : "auto"}
          />
        </div>
      );

    case "date":
      return (
        <div className={styles.dateContainer}>
          <TextField
            type="date"
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={dateValue}
            onChange={e => setDateValue(e.target.value)}
          />
        </div>
      );

    case "text":
      return (
        <div className={styles.textContainer}>
          {responseType.multiline ? (
            <TextField
              variant="outlined"
              multiline
              rows={4}
              fullWidth
              placeholder="Enter your answer..."
              value={textValue}
              onChange={e => setTextValue(e.target.value)}
            />
          ) : (
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter your answer..."
              value={textValue}
              onChange={e => setTextValue(e.target.value)}
            />
          )}
        </div>
      );

    default:
      return null;
  }
};

const QuestionPreview: React.VFC<{ question: Question }> = ({ question }) => (
  <div className={styles.preview}>
    <Box className={styles.questionCard}>
      <Typography variant="subtitle1" className={styles.questionTitle}>
        {question.question}
      </Typography>

      {question.description && (
        <Typography variant="body2" className={styles.questionDescription}>
          {question.description}
        </Typography>
      )}

      <ResponseTypePreview questionId={question.id} responseType={question.responseType} />
    </Box>
  </div>
);

const QuestionPreviewWrapper: React.VFC<{
  id: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  editedQuestion: Question | null;
  showTestToggle: boolean;
}> = ({ id, isExpanded, onToggleExpand, editedQuestion, showTestToggle }) => {
  const { data: fetchedQuestion, isLoading, error } = useQuestion(id);
  const question = editedQuestion || fetchedQuestion;
  const [responses, setResponses] = React.useState<Record<number, ResponseValue>>({});
  const [autoFillTrigger, setAutoFillTrigger] = React.useState(0);
  const [clearTrigger, setClearTrigger] = React.useState(0);
  const [testMode, setTestMode] = React.useState(false);
  const [semanticKeyMap] = React.useState<Record<number, string>>({});

  const setResponse = React.useCallback((questionId: number, value: ResponseValue) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const registerQuestion = React.useCallback((_questionId: number, _semanticKey: string | undefined) => {
    // Not used in single question preview since it doesn't have conditional logic
  }, []);

  const handleDownload = () => {
    if (question) {
      const output = {
        questionId: question.id,
        questionText: question.question,
        response: responses[question.id] ?? null,
      };
      downloadJson(output, `response-${question.id}.json`);
    }
  };

  const handleAutoFill = () => {
    setAutoFillTrigger(prev => prev + 1);
  };

  const handleClear = () => {
    setClearTrigger(prev => prev + 1);
  };

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography color="error">Failed to load question</Typography>;
  if (!question) return null;

  return (
    <ResponsesContext.Provider value={{ responses, setResponse, autoFillTrigger, clearTrigger, registerQuestion, testMode, semanticKeyMap, allQuestions: [] }}>
      <div className={styles.previewContainer}>
        <Box display="flex" justifyContent="space-between" alignItems="center" pr={1} pt={1} pl={1} style={{ gap: 8 }}>
          {showTestToggle && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Test mode"
            />
          )}
          <Box display="flex" alignItems="center" style={{ gap: 8, marginLeft: showTestToggle ? 0 : 'auto' }}>
            <Button size="small" variant="outlined" onClick={handleAutoFill}>
              Auto Fill
            </Button>
            <Button size="small" variant="outlined" onClick={handleClear}>
              Clear
            </Button>
            <Tooltip title="Download Responses">
              <IconButton size="small" onClick={handleDownload}>
                <GetApp />
              </IconButton>
            </Tooltip>
            <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
              <IconButton size="small" onClick={onToggleExpand}>
                {isExpanded ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <QuestionPreview question={question} />
      </div>
    </ResponsesContext.Provider>
  );
};

const PartPreview: React.VFC<{
  part:
    | {
        _type: "question";
        questionId: number;
        visibility: "visible" | { visibleWhen: string };
        requirement: "optional" | "required" | { requiredWhen: string };
      }
    | { _type: "question_group"; questionGroup: number; visibility: "visible" | { visibleWhen: string } };
}> = ({ part }) => {
  if (part._type === "question") {
    return (
      <QuestionInGroup
        questionId={part.questionId}
        qRef={{ visibility: part.visibility, requirement: part.requirement }}
      />
    );
  }

  if (part._type === "question_group") {
    return <QuestionGroupInBook questionGroupId={part.questionGroup} visibility={part.visibility} />;
  }

  return null;
};

const QuestionGroupInBook: React.VFC<{ questionGroupId: number; visibility: "visible" | { visibleWhen: string } }> = ({
  questionGroupId,
  visibility,
}) => {
  const { data: group, isLoading, error } = useQuestionGroup(questionGroupId);

  if (isLoading) return <CircularProgress size={20} />;
  if (error || !group)
    return <Typography color="error">Question group not found: {questionGroupId}</Typography>;

  const isVisibilityConditional = typeof visibility === "object";

  return (
    <Box className={styles.questionGroupInBook}>
      {group.description && (
        <Typography variant="body2" className={styles.groupDescription}>
          {group.description}
        </Typography>
      )}

      {group.questions.map((qRef, idx) => (
        <QuestionInGroup key={idx} questionId={qRef.questionId} qRef={qRef} nested />
      ))}

      {isVisibilityConditional && (
        <Box className={styles.badgeContainer}>
          <div className={styles.conditionalBadge}>
            Visible when: {(visibility as { visibleWhen: string }).visibleWhen}
          </div>
        </Box>
      )}
    </Box>
  );
};

const BookPreviewContent: React.VFC<{ id: number; editedBook: Book | null }> = ({ id, editedBook }) => {
  const { data: fetchedBook, isLoading, error } = useBook(id);
  const book = editedBook || fetchedBook;
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);

  React.useEffect(() => {
    setCurrentPageIndex(0);
  }, [id]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography color="error">Failed to load book</Typography>;
  if (!book) return null;

  const currentPage = book.pages[currentPageIndex];

  return (
    <div className={styles.preview}>
      {book.description && (
        <Typography variant="body2" className={styles.groupDescription}>
          {book.description}
        </Typography>
      )}

      <div className={styles.pageNav}>
        {book.pages.map((page, idx) => (
          <button
            key={idx}
            className={`${styles.pageNavButton} ${idx === currentPageIndex ? styles.pageNavButtonActive : ""}`}
            onClick={() => setCurrentPageIndex(idx)}
            title={page.title ?? "Untitled"}
          >
            {idx + 1}
          </button>
        ))}
      </div>

      <Typography variant="h6" className={styles.pageTitle}>
        {currentPage?.title ?? "Untitled"}
      </Typography>

      {currentPage?.parts.map((part, partIdx) => (
        <PartPreview key={partIdx} part={part} />
      ))}

      <div className={styles.pageNavButtons}>
        <button
          className={styles.navButton}
          onClick={() => setCurrentPageIndex(currentPageIndex - 1)}
          disabled={currentPageIndex === 0}
        >
          ← Previous
        </button>
        <span className={styles.pageIndicator}>
          Page {currentPageIndex + 1} of {book.pages.length}
        </span>
        <button
          className={styles.navButton}
          onClick={() => setCurrentPageIndex(currentPageIndex + 1)}
          disabled={currentPageIndex === book.pages.length - 1}
        >
          Next →
        </button>
      </div>

      {book.footer && (
        <Typography variant="caption" className={styles.bookFooter}>
          {book.footer}
        </Typography>
      )}
    </div>
  );
};

const BookPreview: React.VFC<{
  id: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  editedBook: Book | null;
  showTestToggle: boolean;
}> = ({ id, isExpanded, onToggleExpand, editedBook, showTestToggle }) => {
  const { data: fetchedBook } = useBook(id);
  const book = editedBook || fetchedBook;
  const [responses, setResponses] = React.useState<Record<number, ResponseValue>>({});
  const [autoFillTrigger, setAutoFillTrigger] = React.useState(0);
  const [clearTrigger, setClearTrigger] = React.useState(0);
  const [testMode, setTestMode] = React.useState(false);
  const [semanticKeyMap, setSemanticKeyMap] = React.useState<Record<number, string>>({});

  const setResponse = React.useCallback((questionId: number, value: ResponseValue) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const registerQuestion = React.useCallback((questionId: number, semanticKey: string | undefined) => {
    if (semanticKey) {
      setSemanticKeyMap(prev => ({ ...prev, [questionId]: semanticKey }));
    }
  }, []);

  // Build flat list of all questions from book structure
  const [allQuestions, setAllQuestions] = React.useState<QuestionRefWithId[]>([]);

  React.useEffect(() => {
    if (!book) {
      setAllQuestions([]);
      return;
    }

    const buildQuestionsList = async () => {
      const questions: QuestionRefWithId[] = [];

      for (const page of book.pages) {
        for (const part of page.parts) {
          if (part._type === "question") {
            questions.push({
              questionId: part.questionId,
              visibility: part.visibility,
              requirement: part.requirement,
            });
          } else if (part._type === "question_group") {
            // Fetch the question group to get its questions
            try {
              const groupData = await healthFormsApi.questionGroups.get(part.questionGroup);
              groupData.questions.forEach(qRef => {
                questions.push({
                  questionId: qRef.questionId,
                  visibility: qRef.visibility,
                  requirement: qRef.requirement,
                });
              });
            } catch (error) {
              console.error(`Failed to fetch question group ${part.questionGroup}:`, error);
            }
          }
        }
      }
      setAllQuestions(questions);
    };

    buildQuestionsList();
  }, [book]);

  const handleDownload = () => {
    if (book) {
      const questionIds = new Set<number>();
      book.pages.forEach(page => {
        page.parts.forEach(part => {
          if (part._type === "question") {
            questionIds.add(part.questionId);
          }
        });
      });
      const output = {
        bookId: book.id,
        bookName: book.name,
        responses: Array.from(questionIds).map(qId => ({
          questionId: qId,
          response: responses[qId] ?? null,
        })),
      };
      downloadJson(output, `response-book-${book.id}.json`);
    }
  };

  const handleAutoFill = () => {
    setAutoFillTrigger(prev => prev + 1);
  };

  const handleClear = () => {
    setClearTrigger(prev => prev + 1);
  };

  return (
    <ResponsesContext.Provider value={{ responses, setResponse, autoFillTrigger, clearTrigger, registerQuestion, testMode, semanticKeyMap, allQuestions }}>
      <div className={styles.previewContainer}>
        <Box display="flex" justifyContent="space-between" alignItems="center" pr={1} pt={1} pl={1} style={{ gap: 8 }}>
          {showTestToggle && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Test mode"
            />
          )}
          <Box display="flex" alignItems="center" style={{ gap: 8, marginLeft: showTestToggle ? 0 : 'auto' }}>
            <Button size="small" variant="outlined" onClick={handleAutoFill}>
              Auto Fill
            </Button>
            <Button size="small" variant="outlined" onClick={handleClear}>
              Clear
            </Button>
            <Tooltip title="Download Responses">
              <IconButton size="small" onClick={handleDownload}>
                <GetApp />
              </IconButton>
            </Tooltip>
            <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
              <IconButton size="small" onClick={onToggleExpand}>
                {isExpanded ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <BookPreviewContent id={id} editedBook={editedBook} />
      </div>
    </ResponsesContext.Provider>
  );
};

const QuestionInGroup: React.VFC<{
  questionId: number;
  qRef: {
    visibility: "visible" | { visibleWhen: string };
    requirement: "optional" | "required" | { requiredWhen: string };
  };
  nested?: boolean;
}> = ({ questionId, qRef, nested }) => {
  const { data: question, isLoading, error } = useQuestion(questionId);
  const { responses, semanticKeyMap, testMode, registerQuestion, allQuestions } = useResponses();
  const { evaluatePredicate } = useFeelEvaluator(responses, semanticKeyMap, testMode);

  const isVisibilityConditional = typeof qRef.visibility === "object";
  const isRequirementConditional = typeof qRef.requirement === "object";

  // Evaluate visibility (do this before hooks)
  const isVisible = question && (qRef.visibility === "visible"
    ? true
    : evaluatePredicate((qRef.visibility as { visibleWhen: string }).visibleWhen));

  // Evaluate requirement
  const isRequired = question && (qRef.requirement === "required"
    ? true
    : qRef.requirement === "optional"
    ? false
    : evaluatePredicate((qRef.requirement as { requiredWhen: string }).requiredWhen));

  // Register question with its semanticKey
  React.useEffect(() => {
    if (question?.semanticKey) {
      registerQuestion(question.id, question.semanticKey);
    }
  }, [question?.id, question?.semanticKey, registerQuestion]);

  if (isLoading) return <CircularProgress size={20} />;
  if (error || !question) return <Typography color="error">Question not found: {questionId}</Typography>;

  // Hide question if not visible based on visibleWhen condition
  if (!isVisible) {
    return null;
  }

  // Test mode logic: Hide future unanswered questions except the first one
  const shouldShow = shouldShowQuestionInTestMode(
    question.id,
    testMode,
    allQuestions,
    responses,
    evaluatePredicate
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <Box className={nested ? styles.questionNested : styles.questionInGroup}>
      <Box className={styles.questionHeader}>
        <Typography variant="subtitle1" className={styles.questionTitle}>
          {question.question}
        </Typography>
      </Box>

      {question.description && (
        <Typography variant="body2" className={styles.questionDescription}>
          {question.description}
        </Typography>
      )}

      {question.responseType ? (
        <ResponseTypePreview questionId={question.id} responseType={question.responseType} />
      ) : (
        <Typography color="error" variant="body2">Question responseType is missing</Typography>
      )}

      <Box className={styles.badgeContainer}>
        {isRequired && <span className={styles.requiredBadge}>Required</span>}
        {isRequirementConditional && !testMode && (
          <span className={styles.conditionalRequiredBadge}>
            Required when: {(qRef.requirement as { requiredWhen: string }).requiredWhen}
          </span>
        )}
        {question.semanticKey && (
          <span
            className={styles.semanticKeyBadge}
            onClick={() => navigator.clipboard.writeText(question.semanticKey!)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            title="Click to copy"
          >
            {question.semanticKey}
          </span>
        )}
        {isVisibilityConditional && !testMode && (
          <div className={styles.conditionalBadge}>
            Visible when: {(qRef.visibility as { visibleWhen: string }).visibleWhen}
          </div>
        )}
      </Box>
    </Box>
  );
};

const QuestionGroupPreviewContent: React.VFC<{ id: number; editedGroup: QuestionGroup | null }> = ({
  id,
  editedGroup,
}) => {
  const { data: fetchedGroup, isLoading, error } = useQuestionGroup(id);
  const group = editedGroup || fetchedGroup;

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography color="error">Failed to load question group</Typography>;
  if (!group) return null;

  return (
    <div className={styles.preview}>
      {group.description && (
        <Typography variant="body2" className={styles.groupDescription}>
          {group.description}
        </Typography>
      )}

      {group.questions.map((qRef, idx) => (
        <QuestionInGroup key={idx} questionId={qRef.questionId} qRef={qRef} />
      ))}
    </div>
  );
};

const QuestionGroupPreview: React.VFC<{
  id: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  editedGroup: QuestionGroup | null;
  showTestToggle: boolean;
}> = ({ id, isExpanded, onToggleExpand, editedGroup, showTestToggle }) => {
  const { data: fetchedGroup } = useQuestionGroup(id);
  const group = editedGroup || fetchedGroup;
  const [responses, setResponses] = React.useState<Record<number, ResponseValue>>({});
  const [autoFillTrigger, setAutoFillTrigger] = React.useState(0);
  const [clearTrigger, setClearTrigger] = React.useState(0);
  const [testMode, setTestMode] = React.useState(false);
  const [semanticKeyMap, setSemanticKeyMap] = React.useState<Record<number, string>>({});

  const setResponse = React.useCallback((questionId: number, value: ResponseValue) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const registerQuestion = React.useCallback((questionId: number, semanticKey: string | undefined) => {
    if (semanticKey) {
      setSemanticKeyMap(prev => ({ ...prev, [questionId]: semanticKey }));
    }
  }, []);

  // Build flat list of all questions from group structure
  const allQuestions = React.useMemo(() => {
    if (!group) return [];
    return group.questions.map(qRef => ({
      questionId: qRef.questionId,
      visibility: qRef.visibility,
      requirement: qRef.requirement,
    }));
  }, [group]);

  const handleDownload = () => {
    if (group) {
      const output = {
        questionGroupId: group.id,
        questionGroupName: group.name,
        responses: group.questions.map(qRef => ({
          questionId: qRef.questionId,
          response: responses[qRef.questionId] ?? null,
        })),
      };
      downloadJson(output, `response-group-${group.id}.json`);
    }
  };

  const handleAutoFill = () => {
    setAutoFillTrigger(prev => prev + 1);
  };

  const handleClear = () => {
    setClearTrigger(prev => prev + 1);
  };

  return (
    <ResponsesContext.Provider value={{ responses, setResponse, autoFillTrigger, clearTrigger, registerQuestion, testMode, semanticKeyMap, allQuestions }}>
      <div className={styles.previewContainer}>
        <Box display="flex" justifyContent="space-between" alignItems="center" pr={1} pt={1} pl={1} style={{ gap: 8 }}>
          {showTestToggle && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Test mode"
            />
          )}
          <Box display="flex" alignItems="center" style={{ gap: 8, marginLeft: showTestToggle ? 0 : 'auto' }}>
            <Button size="small" variant="outlined" onClick={handleAutoFill}>
              Auto Fill
            </Button>
            <Button size="small" variant="outlined" onClick={handleClear}>
              Clear
            </Button>
            <Tooltip title="Download Responses">
              <IconButton size="small" onClick={handleDownload}>
                <GetApp />
              </IconButton>
            </Tooltip>
            <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
              <IconButton size="small" onClick={onToggleExpand}>
                {isExpanded ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <QuestionGroupPreviewContent id={id} editedGroup={editedGroup} />
      </div>
    </ResponsesContext.Provider>
  );
};

const Preview: React.VFC<PreviewProps> = ({ selection, isExpanded, onToggleExpand, editedEntity, showTestToggle }) => {
  if (!selection) {
    return (
      <div className={styles.empty}>
        <Typography color="textSecondary">Select an item to preview</Typography>
      </div>
    );
  }

  if (selection.type === "question") {
    const editedQuestion =
      editedEntity && "_type" in editedEntity === false && "responseType" in editedEntity
        ? (editedEntity as Question)
        : null;
    return (
      <QuestionPreviewWrapper
        id={selection.id}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        editedQuestion={editedQuestion}
        showTestToggle={showTestToggle}
      />
    );
  }

  if (selection.type === "book") {
    const editedBook = editedEntity && "pages" in editedEntity ? (editedEntity as Book) : null;
    return (
      <BookPreview
        id={selection.id}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        editedBook={editedBook}
        showTestToggle={showTestToggle}
      />
    );
  }

  if (selection.type === "questionGroup") {
    const editedGroup =
      editedEntity && "questions" in editedEntity && !("pages" in editedEntity)
        ? (editedEntity as QuestionGroup)
        : null;
    return (
      <QuestionGroupPreview
        id={selection.id}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        editedGroup={editedGroup}
        showTestToggle={showTestToggle}
      />
    );
  }

  return null;
};

export default Preview;

const styles = {
  previewContainer: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  `,
  preview: css`
    padding: 16px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  `,
  empty: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  `,
  questionCard: css`
    margin-top: 16px;
    padding: 20px;
    background: #e7f3ff;
    border-radius: 8px;
  `,
  questionTitle: css`
    font-size: 17px !important;
    font-weight: 700 !important;
    margin-bottom: 8px;
    color: #333;
  `,
  questionDescription: css`
    font-size: 14px;
    color: #666;
    margin-bottom: 16px;
  `,
  semanticKeyBadge: css`
    font-size: 12px;
    color: #28a745;
    background: #d4edda;
    padding: 6px 10px;
    border-radius: 4px;
    margin-bottom: 12px;
    font-family: monospace;
    display: inline-block;
  `,
  selectContainer: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
  `,
  radioOption: css`
    display: flex;
    align-items: center;
    padding: 8px;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #f8f9fa;
    }
  `,
  radioButton: css`
    padding: 0;
    margin-right: 12px;
  `,
  radioLabel: css`
    flex: 1;
    font-size: 15px;
    color: #495057;
    line-height: 1.5;
  `,
  otherField: css`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
  `,
  multiSelectContainer: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
  `,
  groupSection: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
    }
  `,
  groupHeader: css`
    font-weight: 600 !important;
    font-size: 14px !important;
    margin-bottom: 8px !important;
    color: #333;
  `,
  checkboxOption: css`
    display: flex;
    align-items: center;
    padding: 8px;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #f8f9fa;
    }
  `,
  checkboxButton: css`
    padding: 0;
    margin-right: 12px;
  `,
  checkboxLabel: css`
    flex: 1;
    font-size: 15px;
    color: #495057;
    line-height: 1.5;
  `,
  booleanContainer: css`
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-top: 8px;
  `,
  booleanOption: css`
    display: flex;
    align-items: center;
    padding: 8px;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
    flex: 1;

    &:hover {
      background: #f8f9fa;
    }
  `,
  vasContainer: css`
    padding: 16px 0;
  `,
  vasLabels: css`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 14px;
    color: #666;
  `,
  integerContainer: css`
    padding: 8px 0;
    max-width: 200px;
  `,
  numberRangeContainer: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
  `,
  rangeSeparator: css`
    color: #666;
    font-size: 14px;
  `,
  dateContainer: css`
    padding: 8px 0;
    max-width: 200px;
  `,
  textContainer: css`
    padding: 8px 0;
  `,
  bookCard: css`
    margin-top: 16px;
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fafafa;
  `,
  groupCard: css`
    margin-top: 16px;
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fafafa;
  `,
  pageSection: css`
    margin-top: 16px;
    padding: 12px;
    background: white;
    border-radius: 4px;
  `,
  partItem: css`
    display: flex;
    align-items: center;
    padding: 8px;
    margin: 4px 0;
    background: #f5f5f5;
    border-radius: 4px;
  `,
  questionRefItem: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    margin: 4px 0;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  `,
  questionInGroup: css`
    margin-bottom: 24px;
    padding: 20px;
    background: #e7f3ff;
    border-radius: 8px;
  `,
  questionNested: css`
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0;
    }
  `,
  questionHeader: css`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
  `,
  badgeContainer: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-top: 12px;
  `,
  conditionalBadge: css`
    font-size: 12px;
    color: #0066cc;
    background: white;
    padding: 6px 10px;
    border-radius: 4px;
    margin-bottom: 12px;
    font-family: monospace;
  `,
  requiredBadge: css`
    font-size: 12px;
    color: #dc3545;
    background: #f8d7da;
    padding: 6px 10px;
    border-radius: 4px;
    font-weight: 500;
  `,
  conditionalRequiredBadge: css`
    font-size: 12px;
    color: #856404;
    background: #fff3cd;
    padding: 6px 10px;
    border-radius: 4px;
    font-family: monospace;
  `,
  groupDescription: css`
    font-size: 14px;
    color: #666;
    margin-bottom: 16px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 4px;
  `,
  questionGroupInBook: css`
    margin-bottom: 24px;
    padding: 20px;
    background: #e7f3ff;
    border-radius: 8px;
  `,
  pageNav: css`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    padding: 12px 0;
    justify-content: center;
  `,
  pageNavButton: css`
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: #e0e0e0;
    color: #666;
    cursor: pointer;
    font-weight: bold;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      background: #bbdefb;
    }
  `,
  pageNavButtonActive: css`
    background: #2196f3;
    color: white;

    &:hover {
      background: #1976d2;
    }
  `,
  pageTitle: css`
    color: #2196f3;
    margin-bottom: 12px !important;
    text-align: center;
  `,
  pageNavButtons: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
  `,
  navButton: css`
    padding: 8px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: white;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: #f5f5f5;
    }
  `,
  pageIndicator: css`
    color: #666;
    font-size: 14px;
  `,
  bookFooter: css`
    display: block;
    margin-top: 16px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 4px;
    color: #666;
  `,
};
